import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { ReportFilters, defaultReportFilters, type ReportFilterState } from './report-filters';
import { exportStockLedger } from '@/lib/export-stock-reports';

const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export function StockLedgerReport() {
  const { t } = useTranslation('trading');
  const [filters, setFilters] = useState<ReportFilterState>(defaultReportFilters);

  const { data, isLoading } = trpc.stockReports.getStockMovementLedger.useQuery({
    dateFrom: new Date(filters.dateFrom),
    dateTo: new Date(filters.dateTo + 'T23:59:59'),
    warehouseId: filters.warehouseId || undefined,
    brandId: filters.brandId || undefined,
    type: (filters.type || undefined) as never,
    search: filters.search || undefined,
    sortBy: filters.sortBy,
  });

  const items = data?.items ?? [];
  const totals = data?.totals;

  return (
    <div className="space-y-4">
      <ReportFilters
        value={filters}
        onChange={setFilters}
        show={{ warehouse: true, brand: true, type: true, sort: true, search: true }}
        onExport={() => totals && exportStockLedger(items, totals)}
        exporting={false}
        exportDisabled={!items.length}
      />

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.date', 'Tarih')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.voucherNo', 'Fiş No')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.movementType', 'Fiş Tipi')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.department', 'Departman')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.code', 'Kod')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.product', 'Ürün')}</th>
              <th className="px-3 py-2.5 text-left font-medium">{t('reports.brand', 'Marka')}</th>
              <th className="px-3 py-2.5 text-right font-medium">{t('reports.inQty', 'Giren')}</th>
              <th className="px-3 py-2.5 text-right font-medium">{t('reports.outQty', 'Çıkan')}</th>
              <th className="px-3 py-2.5 text-right font-medium">{t('reports.inValue', 'Giren Değer')} (TL)</th>
              <th className="px-3 py-2.5 text-right font-medium">{t('reports.outValue', 'Çıkan Değer')} (TL)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={11} className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">{t('reports.noResults', 'Kayıt bulunamadı')}</td></tr>
            )}
            {items.map((m) => (
              <tr key={m.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="whitespace-nowrap px-3 py-2">{new Date(m.movementDate).toLocaleDateString('tr-TR')}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{m.voucherNo}</td>
                <td className="whitespace-nowrap px-3 py-2">{m.typeLabel}</td>
                <td className="px-3 py-2">{m.departmentName}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{m.productCode}</td>
                <td className="max-w-[260px] px-3 py-2"><span className="block truncate">{m.productName}</span></td>
                <td className="px-3 py-2">{m.brandName}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.inQty > 0 ? m.inQty : ''}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.outQty > 0 ? m.outQty : ''}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.inValue > 0 ? fmt(m.inValue) : ''}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.outValue > 0 ? fmt(m.outValue) : ''}</td>
              </tr>
            ))}
          </tbody>
          {totals && items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 bg-muted/40 font-semibold">
                <td className="px-3 py-2.5" colSpan={7}>{t('reports.grandTotal', 'GENEL TOPLAM')}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{totals.inQty}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{totals.outQty}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(totals.inValue)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(totals.outValue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
